/**
 * Runcards Design Tokens
 * 
 * This file mirrors the mobile app's design system (mobile/src/constants/index.js)
 * to ensure visual consistency across platforms.
 * 
 * SOURCE OF TRUTH: mobile/src/constants/index.js
 * 
 * When updating design tokens:
 * 1. Update mobile/src/constants/index.js first
 * 2. Then sync changes to this file
 * 
 * This allows each platform to extend/override as needed while maintaining
 * a consistent base design system.
 */

/**
 * UI Design Constants
 * Mirrors mobile/src/constants/index.js UI object
 */
export const UI = {
  COLORS: {
    // New Runcards Color Palette
    BACKGROUND: '#222531', // Night Tempo - Primary background
    FOREGROUND: '#323645', // Steady Slate - Cards and elevated surfaces
    ACCENT: '#787f9a', // Cool Momentum - Accent color
    WHITE: '#FFFFFF', // Clarity White
    BLACK: '#000000', // Pure black
    
    // Semantic colors
    PRIMARY: '#FFFFFF', // White for primary elements
    SECONDARY: '#787f9a', // Cool Momentum for secondary elements
    SUCCESS: '#10B981', // Green for success
    RELIABILITY_BYLINE: '#3A6A4C', // Verified-green for Reliability Index byline (Confirmed N times)
    WARNING: '#F59E0B', // Orange for warnings
    ERROR: '#E53E3E', // Red for errors
    
    SURFACE: '#323645', // Steady Slate for surfaces
    SURFACE_LIGHT: 'rgba(50, 54, 69, 0.5)', // Translucent foreground
    
    TEXT_PRIMARY: '#FFFFFF', // White text
    TEXT_SECONDARY: '#787f9a', // Cool Momentum text
    TEXT_TERTIARY: 'rgba(255, 255, 255, 0.6)', // Dimmed white
    TEXT_ON_LIGHT: '#000000', // Black text on white backgrounds
    
    BORDER: 'rgba(255, 255, 255, 0.1)', // Subtle border
    BORDER_LIGHT: 'rgba(255, 255, 255, 0.05)', // Very subtle border
    BORDER_MEDIUM: 'rgba(255, 255, 255, 0.2)', // Medium border
    
    ACCENT_HOVER: '#8E8E93', // Grey hover state
  },
  SPACING: {
    XS: 4,
    SM: 8,
    MD: 16,
    LG: 24,
    XL: 32,
    XXL: 48,
  },
  BORDER_RADIUS: {
    SM: 4,
    MD: 8,
    LG: 12,
    XL: 16,
    ROUND: 50,
  },
  FONT_SIZES: {
    XS: 12,
    SM: 14,
    MD: 16,
    LG: 18,
    XL: 20,
    XXL: 24,
    TITLE: 28,
  },
  // Font family - centralized for easy replacement
  // Primary: CreatoDisplay - custom font loaded from assets
  // To change fonts app-wide, just update PRIMARY below
  FONT_FAMILY: {
    PRIMARY: 'CreatoDisplay-Medium', // Medium weight - primary font for body text
    BOLD: 'CreatoDisplay-Bold', // Bold weight - for titles (meeting titles, club names, etc.)
    // Alternative weights available:
    // PRIMARY: 'CreatoDisplay-Regular', // Regular weight
    // PRIMARY: 'CreatoDisplay-ExtraBold', // Extra bold weight for emphasis
    // PRIMARY: 'CreatoDisplay-Light', // Light weight
    // PRIMARY: 'CreatoDisplay-Thin', // Thin weight
    // PRIMARY: 'CreatoDisplay-Black', // Black weight (heaviest)
  },
};

/**
 * Helper function to convert hex color to RGB for CSS variables
 * @param {string} hex - Hex color code (e.g., '#222531')
 * @returns {string} - RGB string (e.g., '34, 37, 49')
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null;
};

/**
 * Helper function to convert hex color to HSL for Tailwind
 * @param {string} hex - Hex color code
 * @returns {string} - HSL string for Tailwind (e.g., '220 14% 20%')
 */
export const hexToHsl = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
};

/**
 * Export design tokens in formats useful for different contexts
 */
export default {
  UI,
  hexToRgb,
  hexToHsl,
};

